import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { Skeleton } from "@trillion/ui/components/ui/skeleton";
import { adminApi } from "@/lib/admin-api";
import { UserDetail } from "@/components/users/user-detail";
import { RoleManager } from "@/components/users/role-manager";
import type { User } from "@/types";

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminApi.users.get(userId);
      setUser(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load user."
      );
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return (
    <div className="flex flex-col">
      <AdminHeader
        title={user?.email ?? "User Details"}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Users", href: "/users" },
          { label: user?.email ?? "Loading..." },
        ]}
      />
      <div className="flex flex-col gap-6 p-6">
        {isLoading ? (
          <div className="flex flex-col gap-6">
            <Skeleton className="h-64 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12">
            <p className="text-destructive">{error}</p>
          </div>
        ) : user ? (
          <>
            <UserDetail user={user} onUserUpdated={fetchUser} />
            <RoleManager
              userId={user.user_id}
              roles={user.roles}
              onRolesChanged={fetchUser}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
